import { mixed, number, boolean, object, string } from "yup";
import * as yup from "yup";

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const loginSchema = object({
  pin: string()
    .trim()
    .required("Pin is required")
    .min(4, "Pin must be at least 4 characters long")
    .typeError("Pin must be a valid number"),
});
const emailSchema = object({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
});

const epcLoginSchema = object({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
  password: string()
    .trim()
    .required("Password is required")
    .min(4, "Password must be at least 4 characters long"),
});

const proformaLoginSchema = object({
  name: string()
    .trim()
    .required("Name is required")
    .typeError("Please enter the valid Name"),
  pin: string()
    .trim()
    .required("Password is required")
    .min(4, "Password must be at least 4 characters long")
    .max(6, "Password must be at most 6 characters long"),
});

const proformaFormV1Schema = object({
  clientName: string()
    .trim()
    .required("Client Name is required")
    .typeError("Please enter the valid Name"),
  isClientEmailDisabled: yup.boolean().optional(),

  clientEmailAddress: yup
    .string()
    .email("Invalid email address")
    .test(
      "required-if-enabled",
      "Client email is required",
      function (value) {
        const { isClientEmailDisabled } = this.parent;
        if (!isClientEmailDisabled && !value) {
          return this.createError({ message: "Client email is required" });
        }
        return true;
      }
    ),
  assetManagement: string()
    .typeError("Please enter the valid value")
    .test("required", "Asset Management is required", function (value, context) {
      if (!context.options.context?.isExternal && !value) {
        return this.createError({ message: "Asset Management is required" });
      }
      return true;
    }),

  equityContribution: string()
    .trim()
    .typeError("Please enter the valid Name")
    .test("required", "Equity Amount is required", function (value, context) {
      if (context.options.context?.isEquity && !value) {
        return this.createError({ message: "Equity Contribution is required" });
      }
      return true;
    }),
  ITCAmount: string()
    .trim()
    .test("required", "ITC Amount is required", (value, context) => {
      if (!context.options.context?.isITC && !value) return false;
      return true;
    }),
  adjustedGrossIncome: string()
    .trim()
    .test("required", "Adjusted Gross Income is required", (value, context) => {
      if (context.options.context?.isGross && !value) return false;
      return true;
    })
    .test("numeric", "Adjusted Gross Income must be a number", function (value) {
      if (!value) return true;
      return /^\d+(,\d+)*(\.\d+)?$/.test(value);
    }),
  partnerMarkup: string()
    .trim()
    .test("required", "Affiliate Markup % is required", function (value) {
      if (!value) return this.createError({ message: "Affiliate Markup % is required" });
      if (parseFloat(value.replace("%", "")) > 23)
        return this.createError({ message: "Affiliate Markup % can be at most 23%" });
      return true;
    }),
  year1DepreciationUsed: string().trim(),
  monthlyPlacedInService: string(),
  federalBonusDepreciationPercentage: string().trim(),
  stateSelector: mixed(),
  stateTaxRate: string().trim().required(),
  stateTaxRateYr7to20: string().trim().required(),
  statePTEPayment: string().trim().required(),
  stateBonusDepreciation: boolean().required().oneOf([true, false], "Please select a valid option"),
  stateBonusDepreciationAsFED: boolean().required().oneOf([true, false], "Please select a valid option"),
  stateRevenueSpread: boolean().required().oneOf([true, false], "Please select a valid option"),
  inceptionDevFee: string().trim().required(),
  ITCPercentage: string().trim().required(),
  developerDevFee: string().trim().required(),
  federalMarginalTaxRate: string().trim().required(),
  scenarioName: string().trim().required(),
  contactGroup: mixed(),
});

const proformaFormSchema = object({
  clientName: string()
    .trim()
    .required("Client Name is required")
    .typeError("Please enter the valid Name"),
  isClientEmailDisabled: yup.boolean().optional(),

  clientEmailAddress: yup
    .string()
    .email("Invalid email address")
    .test(
      "required-if-enabled",
      "Client email is required",
      function (value) {
        const { isClientEmailDisabled } = this.parent;
        if (!isClientEmailDisabled && !value) {
          return this.createError({ message: "Client email is required" });
        }
        return true;
      }
    ),

  equityContribution: string()
    .trim()
    .typeError("Please enter the valid Name")
    .test("required", "Equity Amount is required", function (value, context) {
      if (context.options.context?.isEquity && !value) {
        return this.createError({
          message: "Equity Contribution is required",
        });
      }
      if (context.options.context?.isEquity && value) {
        return true;
      }
      return true;
    }),
  ITCAmount: string()
    .trim()
    .test("required", "ITC Amount is required", (value, context) => {
      if (!context.options.context?.isITC && !value) return false;
      if (!context.options.context?.isITC && value) {
        return true;
      }
      return true;
    }),
  adjustedGrossIncome: string()
    .trim()
    .test("required", "Adjusted Gross Income is required", (value, context) => {
      if (context.options.context?.isGross && !value) return false;
      if (context.options.context?.isGross && value) {
        return true;
      }
      return true;
    })
    .test(
      "numeric",
      "Adjusted Gross Income must be a number",
      function (value) {
        if (!value) return true;
        const regex = /^\d+(,\d+)*(\.\d+)?$/;
        return regex.test(value);
      },
    ),
  partnerMarkup: string()
    .trim()
    .test(
      "required",
      "Affiliate Markup % is required",
      function (value) {
        if (!value) {
          return this.createError({ message: "Affiliate Markup % is required" });
        }
        const numericValue = parseFloat(value.replace("%", ""));
        if (numericValue > 23) {
          return this.createError({ message: "Affiliate Markup % can be at most 23%" });
        }
        return true;
      }
    ),
  year1DepreciationUsed: string().trim(),
  monthlyPlacedInService: string(),
  federalBonusDepreciationPercentage: string().trim(),
  stateSelector: mixed(),
  stateTaxRate: string().trim().required(),
  stateTaxRateYr7to20: string().trim().required(),
  statePTEPayment: string().trim().required(),
  stateBonusDepreciation: boolean()
    .required()
    .oneOf([true, false], "Please select a valid option"),
  stateBonusDepreciationAsFED: boolean()
    .required()
    .oneOf([true, false], "Please select a valid option"),
  stateRevenueSpread: boolean()
    .required()
    .oneOf([true, false], "Please select a valid option"),
  inceptionDevFee: string().trim().required(),
  ITCPercentage: string().trim().required(),
  developerDevFee: string().trim().required(),
  federalMarginalTaxRate: string().trim().required(),
  portionPv: string()
    .trim()
    .test("required", "Portion PV is required", function (value, context) {
      if (context.options.context?.isExternal && !value) return false;
      if (context.options.context?.isExternal && value) {
        return true;
      }
      return true;
    }),
  scenarioName: string().trim().required(),
  contactGroup: mixed(),
  singleOrMFJ: string().trim().required(),
  federalRevenueRecognitionYear: number()
    .oneOf([1, 2])
    .default(1),
  stateRevenueRecognitionYear: number()
    .oneOf([1, 2])
    .optional(),
  year2DeferralPossible: string().optional(),
});

const homeownerSchema = object({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
  firstName: string().trim().required("First name is required"),
  lastName: string().trim().required("Last name is required"),
});

const coHostSchema = object().shape({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
  firstName: string().trim().required("First name is required"),
  lastName: string().trim().required("Last name is required"),
  cosignerId: mixed<File>(),
  // .test(
  //   "required",
  //   "Please upload a file",
  //   (file: any) => {
  //     if (file?.[0] && file?.[0].size) return true;
  //     return false;
  //   }
  // ),
});

const siteSchema = object({
  siteAddress: string().trim().required("Site address is required"),
  blendedUtilityRate: string()
    .trim()
    .required("Blended utility rate is required"),
});
const referalSchema = object({
  firstName: string().trim().required("First name is required"),
  lastName: string().trim().required("Last name is required"),
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
  referralType: string().trim().optional(),
});

const ppaProjectSchema = object({
  pvSize: string().trim().required("PV size is required"),
  batterySize: string().trim().required("Battery size is required"),
  yearOneProduction: string().trim().required("Year 1 production is required"),
});

const turnKeyPriceSchema = object({
  turnKeyPrice: string().trim().required("Turn key price is required"),
});

const reportSchema = object({
  softCommitment: string()
    .trim()
    .required("Soft Commitment is Required")
    .typeError("Soft Commitment is Required"),
});

const incomeSchema = object({
  grossIncome: string().trim().required("Gross Income is required"),
  state: string().trim().required("State is required"),
  taxRate: string()
    .trim()
    .required()
    .typeError("Tax Rate must be a valid number"),
});

const forgotPasswordSchema = object({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
});

const resetPasswordSchema = object({
  newPassword: string()
    .trim()
    .required("New password is Required")
    .typeError("New password is Required")
    .min(4, "Password must be at least 4 characters long"),
  repeatPassword: string()
    .trim()
    .required("Repeat password is Required")
    .typeError("Repeat password is Required")
    .min(4, "Password must be at least 4 characters long"),
});

const masterUserPasswordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required("Password is required")
    .test(
      "strong-password",
      "",
      (value) => {
        if (!value) return false;
        const hasLength = value.length >= 8;
        const hasLower = /[a-z]/.test(value);
        const hasUpper = /[A-Z]/.test(value);
        const hasNumber = /[0-9]/.test(value);
        const hasSpecial = /[^A-Za-z0-9]/.test(value);
        const conditionsMet = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
        
        return hasLength && conditionsMet >= 3;
      }
    ),
  repeatPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const editUserSchema = object().shape({
  customerId: mixed<File>().test(
    "required",
    "Please upload a file",
    (file, context) => {
      if (
        context.options.context?.isFileUploaded.customerId ||
        (file && file?.size)
      )
        return true;
      return false;
    }
  ),
  homeOwner: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (
        context.options.context?.isFileUploaded.homeOwner ||
        (file && file?.size)
      )
        return true;
      return false;
    }
  ),
  auth: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (context.options.context?.isFileUploaded.auth || (file && file?.size))
        return true;
      return false;
    }
  ),
  trust: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (context.options.context?.isFileUploaded.trust || (file && file?.size))
        return true;
      return false;
    }
  ),
  utility: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (
        context.options.context?.isFileUploaded.utility ||
        (file && file?.size)
      )
        return true;
      return false;
    }
  ),
  title: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (context.options.context?.isFileUploaded.title || (file && file?.size))
        return true;
      return false;
    }
  ),
  proposal: mixed<File>().test(
    "Atleast 1 file is required",
    "Please upload a file",
    (file, context) => {
      if (
        context.options.context?.isFileUploaded.proposal ||
        (file && file?.size)
      )
        return true;
      return false;
    }
  ),
  // solarId: string().trim().required("Open-solar id is required"),
  // solarIdVerify: string().required("Submit Open-solar id first"),
  solarId: string().trim(),
  solarIdVerify: string(),
});

const entityLoginSchema = object({
  email: string()
    .trim()
    .email("Please Enter the valid email")
    .required("Email is required"),
  pin: string()
    .trim()
    .required("Pin is required")
    .min(4, "Pin must be at least 4 characters long")
    .typeError("Pin must be a valid number"),
});

const entityCommentSchema = object({
  comment: string().required("Please add comment before submission"),
});

const newClientSchema = object({
  firstName: yup
    .string()
    .trim()
    .required("First Name is required")
    .matches(/^[a-zA-Z\s]+$/, "First Name can only contain letters and spaces"),

  lastName: yup
    .string()
    .trim()
    .required("Last Name is required")
    .matches(/^[a-zA-Z\s]+$/, "Last Name can only contain letters and spaces"),

  state: yup.mixed().required("State of residence is required"),

  mobilePhone: yup
    .string()
    .trim()
    .required("Mobile number is required")
    .matches(/^\d+$/, "Mobile number must contain only digits")
    .min(8, "Phone number must be at least 8 digits")
    .max(15, "Phone number must be at most 15 digits"),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email")
    .required("Email is required"),

  annualIncome: yup.mixed().required("Annual Income is required"),

  sourceOfIncome: yup.mixed().required("Source of Income is required"),

  isAdvisor: yup.string().trim().required("Select Yes/No to continue"),
});

const equityContributionSchema = object({
  email: string()
    .trim()
    .email("Please enter a valid email")
    .required("Email is required")
    .typeError("Please enter the valid email"),
  isCorporation: object({
    value: string().trim().required("Required"),
    label: string().trim().required("Required"),
  }).required("Required"),

  businessType: string()
    .trim()
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  companyBuiltState: string()
    .trim()
    .required("Required")
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),

  qualityRegisterNewCompany: string()
    .trim()
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  potentialCompanyName1: string()
    .trim()
    .required("Required")
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  potentialCompanyName2: string()
    .trim()
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  potentialCompanyName3: string()
    .trim()
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  companyMailingAddress: string().trim().required("Required"),
  socialSecurityNumber: string()
    .trim()
    .required("Required")
    .matches(/^[a-zA-Z0-9 ]*$/, "No special characters allowed"),
  registerAgentRequired: object({
    value: string().trim().required("Required"),
    label: string().trim().required("Required"),
  }).required("Required"),
  driverLicense: mixed<FileList>()
    .required("A copy of your Driver's License is required.")
    .test("file", "File is required", (value) => {
      return value?.length !== 0;
    })
    .test("fileSize", "File should be less than 50MB", (value) => {
      if (!value || value.length === 0) return false;
      const MAX_SIZE = 50 * 1024 * 1024; // 50MB
      return (value[0] as File).size <= MAX_SIZE;
    }),
  registerAgentFullName: string().trim(),
  // .required('Required'), // No
  isContractAgentFee: object({
    //YES
    value: string().trim(),
    label: string().trim(),
  }),
  //   registerAgentFullName: string().trim().when(
  //   "registerAgentRequired",
  //   (registerAgentRequired, schema) => {
  //     return registerAgentRequired.value === "NO"
  //       ? schema.required("Required")
  //       : schema;
  //   }
  // ),
  // isContractAgentFee: object({
  //   value: string().trim(),
  //   label: string().trim(),
  // }).when("registerAgentRequired", {
  //   is: { value: "YES" },
  //   then: object().shape({
  //     value: string().trim().required("Required"),
  //     label: string().trim().required("Required"),
  //   }),
  // }),
  membersLegalName: string().trim(),
});

const isoftSchema = object({
  firstName: string().trim().required("First Name is required"),
  middleName: string().trim(),
  lastName: string().trim().required("Last Name is required"),
  address: string().trim().required("Address is required"),
  apt: string().trim(),
  city: string().trim().required("City is required"),
  state: object({
    value: string().trim().required("State is required"),
    label: string().trim().required("State is required"),
  }).required("Required"),
  zip: string().trim().required("ZIP is required"),
  socialSecurity: string().trim(),
  dob: string(),
  phone: string().trim(),
  email: string()
    .trim()
    .email("Please enter a valid email")
    .required("Email is required"),
});

const EntityYesSchema = yup.object({
  entityName: yup
    .string()
    .required("Entity name is required")
    .matches(
      /^[a-zA-Z\s&.,'-]+$/,
      "Entity name can only contain letters, spaces, and basic punctuation"
    ),

  entityType: mixed().required("Entity type is required"),
  stateOfIncorporation: mixed().required("State of incorporation is required"),
  businessTitle: string()
    .required("Business title is required")
    .matches(
      /^[a-zA-Z\s&.,'-]+$/,
      "Business title can only contain letters, spaces, and basic punctuation"
    ),
  mailingStreet: string()
    .required("Mailing street is required")
    .matches(
      /^[a-zA-Z0-9\s&.,'-]+$/,
      "Mailing Street can only contain letters, numbers, spaces, and basic punctuation"
    ),
  mailingCity: string()
    .required("Mailing city is required")
    .matches(
      /^[a-zA-Z\s&.,'-]+$/,
      "Mailing City can only contain letters, spaces, and basic punctuation"
    ),
  mailingCountry: string().required("Mailing country is required"),
  mailingState: mixed().required("Mailing state is required"),
  mailingZip: string()
    .required("Mailing zip is required")
    .matches(/^\d+$/, "Mailing Zip can only contain numbers"),
  mailingSuite: string().matches(
    /^[a-zA-Z0-9\s&.,'-]+$/,
    "Mailing Street can only contain letters, numbers, spaces, and basic punctuation"
  ),
  // formationFile: mixed()
  //   .required("Formation Document Package is required")
  //   .test('fileType', 'File must be a PDF', (value: any) => {
  //     if (value instanceof File) {
  //       return value.type === 'application/pdf';
  //     }
  //     return false;
  //   })
  //   .test('fileSize', 'File size must be less than 10MB', (value: any) => {
  //     if (value instanceof File) {
  //       const MAX_SIZE = 10 * 1024 * 1024;
  //       return value.size <= MAX_SIZE;
  //     }
  //     return false;
  //   })
});
export const EntitySchema = yup.object().shape({
  entityName: yup.string().required("Entity Name is required"),
  entityType: yup
    .object({
      value: yup.string().trim().required("State is required"),
      label: yup.string().trim().required("State is required"),
    })
    .required("Required"), // Ensure the type matches.
  stateOfIncorporation: yup
    .object({
      value: yup.string().trim().required("State is required"),
      label: yup.string().trim().required("State is required"),
    })
    .required("Required"),
  businessTitle: yup.string().required("Business Title is required"),
  mailingStreet: yup.string().required("Mailing Street is required"),
  mailingCity: yup.string().required("Mailing City is required"),
  mailingCountry: yup.string().required("Mailing Country is required"),
  mailingState: yup
    .object({
      value: yup.string().trim().required("State is required"),
      label: yup.string().trim().required("State is required"),
    })
    .required("Required"),
  mailingZip: yup.string().required("Mailing Zip is required"),
});
const noteSchema = object({
  note: string().trim().required("Note is required"),
});

const cpaInfoSchema = object({
  cpaName: string()
    .trim()
    .required("CPA Name is required")
    .matches(/^[A-Za-z\s]+$/, "CPA Name can only contain letters and spaces"),
  cpaEmail: string()
    .trim()
    .email("Entered email must be a valid email")
    .required("CPA Email is required"),
  cpaPhone: string()
    .matches(/^\d+$/, "Phone number must be numeric")
    .min(8, "Phone number must be at least 8 digits")
    .max(15, "Phone number must be at most 15 digits")
    .required("CPA Phone number is required"),
  profilePic: string().optional(),
});

const advisorInfoSchema = yup.object().shape({
  advisorName: yup
    .string()
    .trim()
    .required("Advisor Name is required")
    .matches(
      /^[A-Za-z\s]+$/,
      "Advisor Name can only contain letters and spaces"
    ),
  advisorEmail: yup
    .string()
    .email("Please enter a valid email")
    .required("Advisor email is required"),
  advisorPhone: yup
    .string()
    .required("CPA Phone number is required")
    .matches(/^\d{8,15}$/, "Phone number must be numeric and between 8 to 15 digits")
  ,
});

const bookkeeperInfoSchema = yup.object().shape({
  bookkeeperName: yup
    .string()
    .trim()
    .required("Bookkeeper Name is required")
    .matches(
      /^[A-Za-z\s]+$/,
      "Bookkeeper Name can only contain letters and spaces"
    ),
  bookkeeperEmail: yup
    .string()
    .email("Please enter a valid email")
    .required("Bookkeeper email is required"),
});
const ExistingProformaSchema = yup.object().shape({
  contactEmail: yup
    .string()
    .required("Contact email is required")
    .email("Please enter a valid contact email"),

  proformaId: yup
    .string()
    .required("Proforma ID is required")
});

const contactFormSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be at most 100 characters")
    .matches(
      /^[A-Za-z][A-Za-z\s.'-]*$/,
      "Name can only contain letters, spaces, hyphens, apostrophes, and periods"
    ),
  email: yup
    .string()
    .trim()
    .required("Email is required")
    .email("Please enter a valid email address")
    .max(254, "Email is too long"),
});

const contactClientCareSchema = yup.object().shape({
  firstName: yup.string().required("First Name is required").trim(),
  lastName: yup.string().required("Last Name is required").trim(),
  email: yup.string().email("Enter a valid email").required("Email is required").trim(),
});

const getStartedSchema = object({
  firstName: string()
    .trim()
    .matches(/^[a-zA-Z ]+$/, "First name can only contain letters and spaces")
    .required("First name is required"),
  lastName: string()
    .trim()
    .matches(/^[a-zA-Z ]+$/, "Last name can only contain letters and spaces")
    .required("Last name is required"),
  phone: string()
    .trim()
    .required("Phone number is required")
    .matches(/^\d+$/, "Mobile number must contain only digits")
    .min(8, "Phone number must be at least 8 digits")
    .max(15, "Phone number must be at most 15 digits"),
});

export const incomeLocationSchema = object({
  annualIncome: string()
    .trim()
    .required("Annual income is required"),
  stateOfResidence: string()
    .trim()
    .required("Please select your state of residence"),
});

export {
  loginSchema,
  newClientSchema,
  coHostSchema,
  incomeSchema,
  reportSchema,
  emailSchema,
  siteSchema,
  epcLoginSchema,
  proformaLoginSchema,
  proformaFormV1Schema,
  proformaFormSchema,
  homeownerSchema,
  forgotPasswordSchema,
  ppaProjectSchema,
  turnKeyPriceSchema,
  resetPasswordSchema,
  masterUserPasswordSchema,
  editUserSchema,
  equityContributionSchema,
  entityLoginSchema,
  isoftSchema,
  entityCommentSchema,
  EntityYesSchema,
  noteSchema,
  referalSchema,
  cpaInfoSchema,
  advisorInfoSchema,
  bookkeeperInfoSchema,
  ExistingProformaSchema,
  contactFormSchema,
  contactClientCareSchema,
  getStartedSchema,
};
